import {
  ResolutionDiscount,
  ResolutionExchange,
  ResolutionExchangeCredit,
  ResolutionRefund,
} from "@/pages/(app)/returns/list/components/icons/filters-icons";
import { ShopDto } from "@babackapp/private-api-client/types";
import useTranslation from "@babackapp/translations/use-translation";
import { Globe, HandCoins, MessageCircle } from "lucide-react";
import { ElementType, useMemo } from "react";
import { AnalyticsFilterName } from "../providers/analytics-filters.provider";

type Option = {
  label: string;
  value: string;
  Icon?: ElementType;
  background?: string;
  color?: string;
};

export type AnalyticsFilterDefinitionValue = {
  category: {
    label: string;
    labelPlural: string;
    Icon?: ElementType;
    color?: string;
  };
  isMultiSelect: boolean;
  options: Map<string, Option>;
};

export type AnalyticsFilterDefinition = Map<
  AnalyticsFilterName,
  AnalyticsFilterDefinitionValue
>;

type UseReasonOptionsParams = {
  shop?: ShopDto;
};

function useReasonOptions({ shop }: UseReasonOptionsParams): Option[] {
  return useMemo(() => {
    if (shop) {
      return shop.reasons
        .filter((reason) => reason.active)
        .map((reason) => {
          const lang = shop.preferences.languages[0];
          const reasonValue = reason.values.find(
            (value) => value.lang === lang,
          );

          return {
            value: reason.id!,
            label: reasonValue ? reasonValue.text : reason.values[0].text,
          } satisfies Option;
        });
    }
    return [];
  }, [shop]);
}

function optionToMapOption(options: Option[]): Map<string, Option> {
  return new Map(options.map((option) => [option.value, option]));
}

/**
 * Convert country code to flag emoji
 * e.g., "FR" -> "🇫🇷", "US" -> "🇺🇸"
 */
function countryCodeToFlag(countryCode: string): string {
  if (!countryCode || countryCode.length !== 2) return "";
  const codePoints = countryCode
    .toUpperCase()
    .split("")
    .map((char) => 127397 + char.charCodeAt(0));
  return String.fromCodePoint(...codePoints);
}

/**
 * Get translated country name using Intl.DisplayNames
 */
function getTranslatedCountryName(
  countryCode: string,
  locale: string,
): string {
  try {
    const displayNames = new Intl.DisplayNames([locale], { type: "region" });
    return displayNames.of(countryCode.toUpperCase()) || countryCode;
  } catch {
    return countryCode;
  }
}

type UseAnalyticsFilterDefinitionsParams = {
  shop?: ShopDto;
  availableCountries: { countryCode: string; country: string; ordersCount?: number; returnsCount?: number; returnRate?: number }[];
};

export function useAnalyticsFilterDefinitions({
  shop,
  availableCountries,
}: UseAnalyticsFilterDefinitionsParams): AnalyticsFilterDefinition {
  const t = useTranslation();
  const reasonOptions = useReasonOptions({ shop });

  // Get locale from shop preferences or browser
  const locale = shop?.preferences?.languages?.[0] || navigator.language;

  const countryOptions: Option[] = useMemo(
    () =>
      availableCountries
        .map((item) => ({
          value: item.countryCode,
          label: `${countryCodeToFlag(item.countryCode)} ${getTranslatedCountryName(item.countryCode, locale)}`,
        }))
        .sort((a, b) => a.label.localeCompare(b.label, locale)),
    [availableCountries, locale],
  );

  return useMemo(() => {
    const resolutionOptions = [
      {
        label: t("misc.refund"),
        value: "refund",
        Icon: ResolutionRefund,
      },
      {
        label: t("misc.exchange"),
        value: "exchange",
        Icon: ResolutionExchange,
      },
      {
        label: t("misc.exchange-credit"),
        value: "exchange-credit",
        Icon: ResolutionExchangeCredit,
      },
      {
        label: t("misc.discount"),
        value: "discount",
        Icon: ResolutionDiscount,
      },
    ];

    return new Map([
      [
        "resolution",
        {
          isMultiSelect: true,
          category: {
            label: t("returns.filter.resolution"),
            labelPlural: "resolutions",
            Icon: HandCoins,
          },
          options: optionToMapOption(resolutionOptions),
        },
      ],
      [
        "reason",
        {
          isMultiSelect: true,
          category: {
            label: t("returns.filter.reason"),
            labelPlural: "reasons",
            Icon: MessageCircle,
          },
          options: optionToMapOption(reasonOptions),
        },
      ],
      [
        "country",
        {
          isMultiSelect: true,
          category: {
            label: t("analytics.filter.country"),
            labelPlural: "countries",
            Icon: Globe,
          },
          options: optionToMapOption(countryOptions),
        },
      ],
    ]);
  }, [t, reasonOptions, countryOptions]);
}
